import { pointsToSegment, rawPathToString, stringToRawPath } from './paths'
import { splitBezierAt } from './booleanOps'

// ─── Schneider curve fitting ───────────────────────────────────────────────
// Based on "An Algorithm for Automatically Fitting Digitized Curves" by Philip J. Schneider, Graphics Gems I, 1990. Fits cubic bezier segments to a polyline within an error tolerance.

/**
 * Map smoothness (0-100) to fit error tolerance in pixels. 0 = tight fit (~0.5px tolerance), 100 = very smooth (~8px tolerance).
 */
function smoothnessToTolerance(smoothness) {
	return 0.5 + (smoothness / 100) * 7.5
}

/**
 * Compute chord-length parameterization for points[first..last]. Returns t values in [0,1] proportional to cumulative chord length.
 */
function chordLengthParameterize(pairs, first, last) {
	const u = [0]
	for (let i = first + 1; i <= last; i++) {
		const dx = pairs[i * 2] - pairs[(i - 1) * 2]
		const dy = pairs[i * 2 + 1] - pairs[(i - 1) * 2 + 1]
		u.push(u[u.length - 1] + Math.hypot(dx, dy))
	}
	const total = u[u.length - 1] || 1
	for (let i = 1; i < u.length; i++) u[i] /= total
	return u
}

/**
 * Evaluate a cubic bezier at parameter t.
 */
function bezierEval(p0x, p0y, p1x, p1y, p2x, p2y, p3x, p3y, t) {
	const mt = 1 - t,
		mt2 = mt * mt,
		mt3 = mt2 * mt
	const t2 = t * t,
		t3 = t2 * t
	return [
		mt3 * p0x + 3 * mt2 * t * p1x + 3 * mt * t2 * p2x + t3 * p3x,
		mt3 * p0y + 3 * mt2 * t * p1y + 3 * mt * t2 * p2y + t3 * p3y,
	]
}

/**
 * Evaluate the derivative of a cubic bezier at parameter t.
 */
function bezierDeriv(p0x, p0y, p1x, p1y, p2x, p2y, p3x, p3y, t) {
	const mt = 1 - t,
		mt2 = mt * mt,
		t2 = t * t
	return [
		3 * mt2 * (p1x - p0x) + 6 * mt * t * (p2x - p1x) + 3 * t2 * (p3x - p2x),
		3 * mt2 * (p1y - p0y) + 6 * mt * t * (p2y - p1y) + 3 * t2 * (p3y - p2y),
	]
}

/**
 * Max distance from control points CP1, CP2 to the chord P0→P3. Used as flatness metric for adaptive subdivision.
 */
function cubicFlatness(x0, y0, cp1x, cp1y, cp2x, cp2y, x1, y1) {
	const dx = x1 - x0,
		dy = y1 - y0
	const lenSq = dx * dx + dy * dy
	if (lenSq < 1e-10) return Math.max(Math.hypot(cp1x - x0, cp1y - y0), Math.hypot(cp2x - x0, cp2y - y0))
	const invLen = 1 / Math.sqrt(lenSq)
	const d1 = Math.abs((cp1x - x0) * dy - (cp1y - y0) * dx) * invLen
	const d2 = Math.abs((cp2x - x0) * dy - (cp2y - y0) * dx) * invLen
	return Math.max(d1, d2)
}

// ─── Cubic bezier offset (Tiller-Hanson method with adaptive subdivision) ───

const OFFSET_MAX_ANGLE = Math.PI / 3 // max tangent change per piece before subdividing
const OFFSET_MAX_DEPTH = 6

/**
 * Compute the perpendicular normal (rotated 90° CCW) of a tangent vector. Returns unit normal [nx, ny]. If tangent is zero-length, returns [0, 0].
 */
function tangentNormal(dx, dy) {
	const len = Math.hypot(dx, dy)
	if (len < 1e-10) return [0, 0]
	return [-dy / len, dx / len]
}

/**
 * Angle between two vectors in [0, π].
 */
function angleBetween(ax, ay, bx, by) {
	const la = Math.hypot(ax, ay),
		lb = Math.hypot(bx, by)
	if (la < 1e-10 || lb < 1e-10) return 0
	const dot = (ax * bx + ay * by) / (la * lb)
	return Math.acos(Math.max(-1, Math.min(1, dot)))
}

/**
 * Offset a single cubic bezier by ±hw using the Tiller-Hanson method. Each control point is displaced along the normal at its corresponding parameter. P0 → t=0, CP1 → t=1/3, CP2 → t=2/3, P3 → t=1.
 *
 * This works well when the tangent change across the cubic is small. For larger tangent changes, use offsetCubicAdaptive which subdivides first.
 *
 * @param {number[]} curve - [x0,y0, cp1x,cp1y, cp2x,cp2y, x1,y1]
 * @param {number} hw - half-width (offset distance)
 * @returns {{ left: number[][], right: number[][] }} - left and right offset cubics
 */
export function offsetCubic(curve, hw, hw13, hw23, hwEnd) {
	const [x0, y0, cp1x, cp1y, cp2x, cp2y, x1, y1] = curve

	// Support variable-width offset: 4 hw values at t=0, 1/3, 2/3, 1 When only hw is provided, all four are the same (constant width)
	const hw0 = hw
	const hw1 = hw13 !== undefined ? hw13 : hw
	const hw2 = hw23 !== undefined ? hw23 : hw
	const hw3 = hwEnd !== undefined ? hwEnd : hw

	// Tangent at t=0: 3*(CP1 - P0)
	let d0x = cp1x - x0,
		d0y = cp1y - y0
	// Tangent at t=1: 3*(P3 - CP2)
	let d1x = x1 - cp2x,
		d1y = y1 - cp2y

	// Handle degenerate tangents: if CP1 == P0, use next control leg
	if (Math.hypot(d0x, d0y) < 1e-10) {
		d0x = cp2x - x0
		d0y = cp2y - y0
		if (Math.hypot(d0x, d0y) < 1e-10) {
			d0x = x1 - x0
			d0y = y1 - y0
		}
	}
	if (Math.hypot(d1x, d1y) < 1e-10) {
		d1x = x1 - cp1x
		d1y = y1 - cp1y
		if (Math.hypot(d1x, d1y) < 1e-10) {
			d1x = x1 - x0
			d1y = y1 - y0
		}
	}

	const [n0x, n0y] = tangentNormal(d0x, d0y)
	const [n1x, n1y] = tangentNormal(d1x, d1y)

	// Normals at t=1/3 and t=2/3
	const [dt1x, dt1y] = bezierDeriv(x0, y0, cp1x, cp1y, cp2x, cp2y, x1, y1, 1 / 3)
	const [dt2x, dt2y] = bezierDeriv(x0, y0, cp1x, cp1y, cp2x, cp2y, x1, y1, 2 / 3)
	const [ncp1x, ncp1y] = tangentNormal(dt1x, dt1y)
	const [ncp2x, ncp2y] = tangentNormal(dt2x, dt2y)

	const left = [
		[
			x0 + n0x * hw0,
			y0 + n0y * hw0,
			cp1x + ncp1x * hw1,
			cp1y + ncp1y * hw1,
			cp2x + ncp2x * hw2,
			cp2y + ncp2y * hw2,
			x1 + n1x * hw3,
			y1 + n1y * hw3,
		],
	]

	const right = [
		[
			x0 - n0x * hw0,
			y0 - n0y * hw0,
			cp1x - ncp1x * hw1,
			cp1y - ncp1y * hw1,
			cp2x - ncp2x * hw2,
			cp2y - ncp2y * hw2,
			x1 - n1x * hw3,
			y1 - n1y * hw3,
		],
	]

	return { left, right }
}

/**
 * Offset a cubic bezier with adaptive subdivision. Subdivides until the tangent change per piece is below OFFSET_MAX_ANGLE, then applies Tiller-Hanson offset to each piece.
 *
 * @param {number[]} curve - [x0,y0, cp1x,cp1y, cp2x,cp2y, x1,y1]
 * @param {number} hw - half-width (offset distance)
 * @returns {{ left: number[][], right: number[][] }}
 */
export function offsetCubicAdaptive(curve, hw) {
	const left = []
	const right = []

	function process(c, depth) {
		const [x0, y0, cp1x, cp1y, cp2x, cp2y, x1, y1] = c

		// Tangent at t=0
		let d0x = cp1x - x0,
			d0y = cp1y - y0
		if (Math.hypot(d0x, d0y) < 1e-10) {
			d0x = cp2x - x0
			d0y = cp2y - y0
		}
		if (Math.hypot(d0x, d0y) < 1e-10) {
			d0x = x1 - x0
			d0y = y1 - y0
		}

		// Tangent at t=1
		let d1x = x1 - cp2x,
			d1y = y1 - cp2y
		if (Math.hypot(d1x, d1y) < 1e-10) {
			d1x = x1 - cp1x
			d1y = y1 - cp1y
		}
		if (Math.hypot(d1x, d1y) < 1e-10) {
			d1x = x1 - x0
			d1y = y1 - y0
		}

		const angle = angleBetween(d0x, d0y, d1x, d1y)

		if (angle > OFFSET_MAX_ANGLE && depth < OFFSET_MAX_DEPTH) {
			const { left: lh, right: rh } = splitBezierAt(c, 0.5)
			process(lh, depth + 1)
			process(rh, depth + 1)
		} else {
			const result = offsetCubic(c, hw)
			left.push(result.left[0])
			right.push(result.right[0])
		}
	}

	process(curve, 0)
	return { left, right }
}

/**
 * Use Newton-Raphson to refine parameterization.
 */
function reparameterize(pairs, first, last, u, bezier) {
	const [p0x, p0y, p1x, p1y, p2x, p2y, p3x, p3y] = bezier
	const uPrime = []
	for (let i = first; i <= last; i++) {
		const idx = i - first
		const px = pairs[i * 2],
			py = pairs[i * 2 + 1]
		const [qx, qy] = bezierEval(p0x, p0y, p1x, p1y, p2x, p2y, p3x, p3y, u[idx])
		const [q1x, q1y] = bezierDeriv(p0x, p0y, p1x, p1y, p2x, p2y, p3x, p3y, u[idx])
		// Q''(t) — second derivative
		const mt = 1 - u[idx]
		const q2x = 6 * mt * (p2x - 2 * p1x + p0x) + 6 * u[idx] * (p3x - 2 * p2x + p1x)
		const q2y = 6 * mt * (p2y - 2 * p1y + p0y) + 6 * u[idx] * (p3y - 2 * p2y + p1y)
		const num = (qx - px) * q1x + (qy - py) * q1y
		const den = q1x * q1x + q1y * q1y + (qx - px) * q2x + (qy - py) * q2y
		const tNew = Math.abs(den) > 1e-10 ? u[idx] - num / den : u[idx]
		uPrime.push(Math.max(0, Math.min(1, tNew)))
	}
	return uPrime
}

/**
 * Compute max error between points and a fitted bezier. Returns { maxErr, splitIdx }.
 */
function computeMaxError(pairs, first, last, bezier, u) {
	const [p0x, p0y, p1x, p1y, p2x, p2y, p3x, p3y] = bezier
	let maxErr = 0,
		splitIdx = Math.floor((first + last) / 2)
	for (let i = first; i <= last; i++) {
		const [qx, qy] = bezierEval(p0x, p0y, p1x, p1y, p2x, p2y, p3x, p3y, u[i - first])
		const dx = pairs[i * 2] - qx,
			dy = pairs[i * 2 + 1] - qy
		const err = dx * dx + dy * dy
		if (err > maxErr) {
			maxErr = err
			splitIdx = i
		}
	}
	return { maxErr, splitIdx }
}

/**
 * Fit a single cubic bezier to points[first..last] with given endpoint tangents. Returns [p0x, p0y, p1x, p1y, p2x, p2y, p3x, p3y].
 */
function fitCubic(pairs, first, last, tHat1x, tHat1y, tHat2x, tHat2y, u) {
	const p0x = pairs[first * 2],
		p0y = pairs[first * 2 + 1]
	const p3x = pairs[last * 2],
		p3y = pairs[last * 2 + 1]
	const nPts = last - first + 1

	// Least-squares fit for alpha1, alpha2 (control point distances)
	let c00 = 0,
		c01 = 0,
		c11 = 0,
		x0 = 0,
		x1 = 0
	for (let i = 0; i < nPts; i++) {
		const t = u[i],
			mt = 1 - t
		const b1 = 3 * mt * mt * t
		const b2 = 3 * mt * t * t
		const a1x = tHat1x * b1,
			a1y = tHat1y * b1
		const a2x = tHat2x * b2,
			a2y = tHat2y * b2
		c00 += a1x * a1x + a1y * a1y
		c01 += a1x * a2x + a1y * a2y
		c11 += a2x * a2x + a2y * a2y
		const b0 = mt * mt * mt,
			b3 = t * t * t
		const tmpx = pairs[(first + i) * 2] - (p0x * b0 + p0x * b1 + p3x * b2 + p3x * b3)
		const tmpy = pairs[(first + i) * 2 + 1] - (p0y * b0 + p0y * b1 + p3y * b2 + p3y * b3)
		x0 += a1x * tmpx + a1y * tmpy
		x1 += a2x * tmpx + a2y * tmpy
	}

	const det = c00 * c11 - c01 * c01
	let alpha1, alpha2
	if (Math.abs(det) > 1e-12) {
		alpha1 = (c11 * x0 - c01 * x1) / det
		alpha2 = (c00 * x1 - c01 * x0) / det
	} else {
		const c = Math.hypot(p3x - p0x, p3y - p0y) / 3
		alpha1 = c
		alpha2 = c
	}

	// If alpha is negative or zero, use heuristic
	const segLen = Math.hypot(p3x - p0x, p3y - p0y)
	const epsilon = 1e-6 * segLen
	if (alpha1 < epsilon || alpha2 < epsilon) {
		const c = segLen / 3
		alpha1 = c
		alpha2 = c
	}

	return [
		p0x,
		p0y,
		p0x + tHat1x * alpha1,
		p0y + tHat1y * alpha1,
		p3x + tHat2x * alpha2,
		p3y + tHat2y * alpha2,
		p3x,
		p3y,
	]
}

/**
 * Compute unit tangent at a point by central/forward/backward difference.
 */
function computeTangent(pairs, i, n) {
	let tx, ty
	if (i === 0) {
		tx = pairs[2] - pairs[0]
		ty = pairs[3] - pairs[1]
	} else if (i === n - 1) {
		tx = pairs[i * 2] - pairs[(i - 1) * 2]
		ty = pairs[i * 2 + 1] - pairs[(i - 1) * 2 + 1]
	} else {
		tx = pairs[(i + 1) * 2] - pairs[(i - 1) * 2]
		ty = pairs[(i + 1) * 2 + 1] - pairs[(i - 1) * 2 + 1]
	}
	const len = Math.hypot(tx, ty) || 1
	return [tx / len, ty / len]
}

const MAX_ITERATIONS = 4
const MAX_RECURSION = 16

/**
 * Schneider curve fitting: recursively fit cubic bezier segments. Appends bezier segments to `result` array. Each segment is [p0x, p0y, p1x, p1y, p2x, p2y, p3x, p3y].
 */
function fitCubicRecursive(pairs, first, last, tHat1x, tHat1y, tHat2x, tHat2y, errSq, result, depth) {
	const nPts = last - first + 1
	if (nPts <= 2) {
		// 2-point segment: simple interpolation
		const dist = Math.hypot(pairs[last * 2] - pairs[first * 2], pairs[last * 2 + 1] - pairs[first * 2 + 1]) / 3
		result.push([
			pairs[first * 2],
			pairs[first * 2 + 1],
			pairs[first * 2] + tHat1x * dist,
			pairs[first * 2 + 1] + tHat1y * dist,
			pairs[last * 2] + tHat2x * dist,
			pairs[last * 2 + 1] + tHat2y * dist,
			pairs[last * 2],
			pairs[last * 2 + 1],
		])
		return
	}

	let u = chordLengthParameterize(pairs, first, last)

	for (let iter = 0; iter <= MAX_ITERATIONS; iter++) {
		const bezier = fitCubic(pairs, first, last, tHat1x, tHat1y, tHat2x, tHat2y, u)
		const { maxErr, splitIdx } = computeMaxError(pairs, first, last, bezier, u)
		if (maxErr <= errSq) {
			result.push(bezier)
			return
		}
		if (depth >= MAX_RECURSION) {
			result.push(bezier)
			return
		}
		if (iter < MAX_ITERATIONS) {
			u = reparameterize(pairs, first, last, u, bezier)
		} else {
			// Split at point of max error and recurse
			const [stx, sty] = computeTangent(pairs, splitIdx, pairs.length / 2)
			fitCubicRecursive(pairs, first, splitIdx, tHat1x, tHat1y, -stx, -sty, errSq, result, depth + 1)
			fitCubicRecursive(pairs, splitIdx, last, stx, sty, tHat2x, tHat2y, errSq, result, depth + 1)
			return
		}
	}
}

/**
 * Fit cubic bezier segments to a flat [x,y,...] polyline. Returns a rawPath segment (flat array in the project's format).
 * @param {number[]} pairs - flat [x,y,x,y,...] points
 * @param {number} smoothness - 0..100 (controls error tolerance)
 * @returns {number[]} rawPath segment
 */
export function schneiderFit(pairs, smoothness, startTangent) {
	const n = pairs.length / 2
	if (n < 2) return [pairs[0], pairs[1]]
	if (n === 2) {
		const [t1x, t1y] = startTangent || computeTangent(pairs, 0, n)
		const [t2x, t2y] = computeTangent(pairs, n - 1, n)
		const dist = Math.hypot(pairs[2] - pairs[0], pairs[3] - pairs[1]) / 3
		return [
			pairs[0],
			pairs[1],
			pairs[0] + t1x * dist,
			pairs[1] + t1y * dist,
			pairs[2] - t2x * dist,
			pairs[3] - t2y * dist,
			pairs[2],
			pairs[3],
		]
	}

	const tolerance = smoothnessToTolerance(smoothness)
	const errSq = tolerance * tolerance
	const [t1x, t1y] = startTangent || computeTangent(pairs, 0, n)
	const [t2x, t2y] = computeTangent(pairs, n - 1, n)

	const beziers = []
	fitCubicRecursive(pairs, 0, n - 1, t1x, t1y, -t2x, -t2y, errSq, beziers, 0)

	// Convert to rawPath segment format: [a0x, a0y, cp1x, cp1y, cp2x, cp2y, a1x, a1y, ...]
	if (beziers.length === 0) return [pairs[0], pairs[1]]
	const seg = [beziers[0][0], beziers[0][1]]
	for (const b of beziers) {
		seg.push(b[2], b[3], b[4], b[5], b[6], b[7])
	}
	return seg
}

/**
 * Extract the end tangent (unit vector) from the last cubic of a rawPath segment. Used for incremental fitting — pass as startTangent to the next schneiderFit call.
 */
export function getSegmentEndTangent(segment) {
	if (segment.length < 8) return null
	const len = segment.length
	// Last cubic's cp2 and anchor: segment[len-4], segment[len-3] is cp2; segment[len-2], segment[len-1] is anchor
	const tx = segment[len - 2] - segment[len - 4]
	const ty = segment[len - 1] - segment[len - 3]
	const d = Math.hypot(tx, ty) || 1
	return [tx / d, ty / d]
}

/**
 * Fit a bezier spine and sample it at arc-length intervals for outline construction. Returns flat [x,y,...] pairs evenly distributed along the fitted curve.
 * @param {number[]} pairs - flat [x,y,x,y,...] input points
 * @param {number} smoothness - 0..100
 * @param {number} [interval=6] - approximate spacing between output samples (px)
 * @returns {number[]} flat [x,y,...] sampled pairs
 */
export function fitAndSampleSpine(pairs, smoothness, interval = 6) {
	const segment = schneiderFit(pairs, smoothness)
	return sampleSegmentByArcLength(segment, interval)
}

/**
 * Sample a rawPath segment at regular arc-length intervals. Returns flat [x,y,...] pairs. Always includes first and last points.
 */
function sampleSegmentByArcLength(segment, interval) {
	if (segment.length < 8) return [segment[0], segment[1]]
	// Densely sample first, then pick at arc-length intervals
	const dense = []
	dense.push(segment[0], segment[1])
	for (let i = 2; i < segment.length; i += 6) {
		const x0 = i === 2 ? segment[0] : segment[i - 2]
		const y0 = i === 2 ? segment[1] : segment[i - 1]
		const [cp1x, cp1y, cp2x, cp2y, x1, y1] = [
			segment[i],
			segment[i + 1],
			segment[i + 2],
			segment[i + 3],
			segment[i + 4],
			segment[i + 5],
		]
		const chordLen = Math.hypot(x1 - x0, y1 - y0)
		const ctrlLen =
			Math.hypot(cp1x - x0, cp1y - y0) + Math.hypot(cp2x - cp1x, cp2y - cp1y) + Math.hypot(x1 - cp2x, y1 - cp2y)
		const approxLen = (chordLen + ctrlLen) / 2
		const steps = Math.max(2, Math.ceil(approxLen / 2))
		for (let j = 1; j <= steps; j++) {
			const t = j / steps
			const [px, py] = bezierEval(x0, y0, cp1x, cp1y, cp2x, cp2y, x1, y1, t)
			dense.push(px, py)
		}
	}
	// Now pick at arc-length intervals
	const nDense = dense.length / 2
	const arcLens = [0]
	for (let i = 1; i < nDense; i++) {
		arcLens.push(
			arcLens[i - 1] + Math.hypot(dense[i * 2] - dense[(i - 1) * 2], dense[i * 2 + 1] - dense[(i - 1) * 2 + 1])
		)
	}
	const totalLen = arcLens[nDense - 1]
	if (totalLen < interval) return dense

	const result = [dense[0], dense[1]]
	let nextDist = interval
	for (let i = 1; i < nDense; i++) {
		while (arcLens[i] >= nextDist && nextDist < totalLen - interval * 0.5) {
			const frac = (nextDist - arcLens[i - 1]) / (arcLens[i] - arcLens[i - 1])
			result.push(
				dense[(i - 1) * 2] + frac * (dense[i * 2] - dense[(i - 1) * 2]),
				dense[(i - 1) * 2 + 1] + frac * (dense[i * 2 + 1] - dense[(i - 1) * 2 + 1])
			)
			nextDist += interval
		}
	}
	result.push(dense[(nDense - 1) * 2], dense[(nDense - 1) * 2 + 1])
	return result
}

// ─── Smooth handles (used by interpolateEditAnchors) ───────────────────────

/**
 * Post-process a bezier segment to smooth control point handles. Recalculates handles so curves flow smoothly through anchor points.
 * @param {number[]} segment - flat array [x0,y0, cp1x,cp1y,cp2x,cp2y,x1,y1, ...]
 * @returns {number[]} smoothed segment (mutates in place)
 */
function smoothHandles(segment) {
	if (segment.length < 14) return segment
	const anchors = [{ x: segment[0], y: segment[1] }]
	for (let i = 2; i < segment.length; i += 6) {
		anchors.push({ x: segment[i + 4], y: segment[i + 5] })
	}
	const n = anchors.length
	if (n < 3) return segment
	for (let k = 1; k < n - 1; k++) {
		const prev = anchors[k - 1],
			curr = anchors[k],
			next = anchors[k + 1]
		const tx = next.x - prev.x,
			ty = next.y - prev.y
		const d1 = Math.hypot(curr.x - prev.x, curr.y - prev.y)
		const d2 = Math.hypot(next.x - curr.x, next.y - curr.y)
		if (d1 + d2 < 1e-6) continue
		const tLen = Math.hypot(tx, ty)
		if (tLen < 1e-6) continue
		const ux = tx / tLen,
			uy = ty / tLen
		segment[k * 6 - 2] = curr.x - ux * (d1 / 3)
		segment[k * 6 - 1] = curr.y - uy * (d1 / 3)
		segment[k * 6 + 2] = curr.x + ux * (d2 / 3)
		segment[k * 6 + 3] = curr.y + uy * (d2 / 3)
	}
	const first = anchors[0],
		second = anchors[1]
	if (Math.hypot(second.x - first.x, second.y - first.y) > 1e-6) {
		segment[2] = first.x + (second.x - first.x) / 3
		segment[3] = first.y + (second.y - first.y) / 3
	}
	const last = anchors[n - 1],
		secondLast = anchors[n - 2]
	if (Math.hypot(last.x - secondLast.x, last.y - secondLast.y) > 1e-6) {
		const k = n - 1
		segment[k * 6 - 2] = last.x - (last.x - secondLast.x) / 3
		segment[k * 6 - 1] = last.y - (last.y - secondLast.y) / 3
	}
	return segment
}

// ─── Legacy exports ───────────────────────────────────────────────────────

/**
 * Compute curviness parameter from a 0-100 smoothness value.
 */
export function smoothCurviness(smoothness) {
	return 0.2 + (smoothness / 100) * 1.3
}

/**
 * Re-fit a brush stroke from raw points string.
 * @param {string} rawPointsString - Comma-separated x,y pairs
 * @param {number} smoothness - 0..100
 * @param {string} [strokeCap] - 'round' or 'butt'
 * @param {boolean} variableWidth
 * @param {number} [strokeWidth] - base brush width
 * @returns {{ data: string, centerline: string, x: number, y: number } | null}
 */
export function refitBrushPath(rawPointsString, smoothness, strokeCap, strokeWidth, minWidth = 0, widthProfile) {
	const nums = rawPointsString.split(',').map(Number)
	return fitVariableWidthBrush(nums, smoothness, strokeCap || 'round', strokeWidth, minWidth, widthProfile)
}

// ─── Variable-width brush (filled closed outline) ─────────────────────────

/**
 * Assign position-based half-widths to centerline points. Width follows sin(π*t) — 0 at edges, max at center. For short strokes, the effective max width is clamped so the stroke doesn't bulge wider than its length.
 * @param {number[]} pairs - flat [x,y,x,y,...] centerline points
 * @param {number} baseHalfWidth - maximum half-width at center
 * @param {number} [minHalfWidth=0] - minimum half-width (floor at edges)
 * @returns {number[]} flat [x,y,hw, x,y,hw, ...] triples
 */
export function assignPositionWidths(pairs, baseHalfWidth, minHalfWidth = 0, widthProfile = 'diamond') {
	const n = pairs.length / 2
	if (n < 2) return []

	// Compute cumulative arc lengths
	const arcLengths = [0]
	for (let i = 1; i < n; i++) {
		const dx = pairs[i * 2] - pairs[(i - 1) * 2]
		const dy = pairs[i * 2 + 1] - pairs[(i - 1) * 2 + 1]
		arcLengths.push(arcLengths[i - 1] + Math.hypot(dx, dy))
	}
	const totalLength = arcLengths[n - 1] || 1

	// Limit max width on short strokes to prevent bulging
	const effectiveHW = totalLength < baseHalfWidth * 4 ? totalLength / 4 : baseHalfWidth

	const triples = []
	for (let i = 0; i < n; i++) {
		const t = arcLengths[i] / totalLength
		const factor = widthProfile === 'grow' ? t : widthProfile === 'shrink' ? 1 - t : Math.sin(Math.PI * t)
		const hw = minHalfWidth + (effectiveHW - minHalfWidth) * factor
		triples.push(pairs[i * 2], pairs[i * 2 + 1], hw)
	}
	return triples
}

// ─── Curvature-adaptive decimation ─────────────────────────────────────────

const MAX_ANGLE = 0.28 // ~16° — emit a point when cumulative angle exceeds this
const MAX_DIST = 35 // max distance between emitted points (px)
const MIN_DIST_SQ = 16 // 4px² — never merge below this distance

/**
 * Decimate a dense flat [x,y,...] polyline by curvature and distance. Keeps endpoints, keeps points at tight bends (high curvature), merges points on straight/gentle sections. O(n) single pass.
 * @param {number[]} pairs - flat [x,y,x,y,...] dense samples
 * @returns {number[]} decimated flat pairs
 */
export function decimateByCurvature(pairs) {
	const n = pairs.length / 2
	if (n <= 4) return pairs

	const result = [pairs[0], pairs[1]] // always keep first
	let lastIdx = 0
	let cumAngle = 0
	// Previous tangent direction
	let prevTx = pairs[2] - pairs[0]
	let prevTy = pairs[3] - pairs[1]
	let prevLen = Math.hypot(prevTx, prevTy) || 1

	for (let i = 1; i < n - 1; i++) {
		const tx = pairs[(i + 1) * 2] - pairs[i * 2]
		const ty = pairs[(i + 1) * 2 + 1] - pairs[i * 2 + 1]
		const tLen = Math.hypot(tx, ty) || 1

		// Angle between consecutive tangents (unsigned)
		const dot = (prevTx * tx + prevTy * ty) / (prevLen * tLen)
		const angle = Math.acos(Math.max(-1, Math.min(1, dot)))
		cumAngle += angle

		// Distance from last emitted point
		const dx = pairs[i * 2] - pairs[lastIdx * 2]
		const dy = pairs[i * 2 + 1] - pairs[lastIdx * 2 + 1]
		const distSq = dx * dx + dy * dy

		// Emit if angle threshold exceeded, or max distance exceeded
		const emit = cumAngle >= MAX_ANGLE || distSq >= MAX_DIST * MAX_DIST

		if (emit && distSq >= MIN_DIST_SQ) {
			result.push(pairs[i * 2], pairs[i * 2 + 1])
			lastIdx = i
			cumAngle = 0
		}

		prevTx = tx
		prevTy = ty
		prevLen = tLen
	}

	// Always keep last point
	result.push(pairs[(n - 1) * 2], pairs[(n - 1) * 2 + 1])
	return result
}

// ─── Curved quad segments (Catmull-Rom edges) ──────────────────────────────

/**
 * Build a rawPath segment for quad i with Catmull-Rom curved left/right edges. Cross-edges (left↔right) are straight lines. Path: moveTo(left[i]) → bezier(left[i]→left[i+1]) → line(right[i+1]) →
 *        bezier(right[i+1]→right[i]) → close
 */
function curvedSegment(left, right, i) {
	const n = left.length
	// Catmull-Rom neighbors (clamp at boundaries)
	const lp0 = left[Math.max(0, i - 1)]
	const lp1 = left[i]
	const lp2 = left[i + 1]
	const lp3 = left[Math.min(n - 1, i + 2)]
	const rp0 = right[Math.max(0, i - 1)]
	const rp1 = right[i]
	const rp2 = right[i + 1]
	const rp3 = right[Math.min(n - 1, i + 2)]

	// Catmull-Rom → cubic bezier control points (left edge: p1→p2)
	const lc1x = lp1.x + (lp2.x - lp0.x) / 6
	const lc1y = lp1.y + (lp2.y - lp0.y) / 6
	const lc2x = lp2.x - (lp3.x - lp1.x) / 6
	const lc2y = lp2.y - (lp3.y - lp1.y) / 6

	// Catmull-Rom → cubic bezier control points (right edge: p2→p1, reversed)
	const rc1x = rp2.x + (rp1.x - rp3.x) / 6
	const rc1y = rp2.y + (rp1.y - rp3.y) / 6
	const rc2x = rp1.x - (rp0.x - rp2.x) / 6
	const rc2y = rp1.y - (rp0.y - rp2.y) / 6

	// Build rawPath segment: moveTo + 4 cubic bezier edges
	return [
		lp1.x,
		lp1.y,
		// Edge 1: left[i] → left[i+1] (Catmull-Rom curve)
		lc1x,
		lc1y,
		lc2x,
		lc2y,
		lp2.x,
		lp2.y,
		// Edge 2: left[i+1] → right[i+1] (straight cross-edge)
		lp2.x,
		lp2.y,
		rp2.x,
		rp2.y,
		rp2.x,
		rp2.y,
		// Edge 3: right[i+1] → right[i] (Catmull-Rom curve, reversed)
		rc1x,
		rc1y,
		rc2x,
		rc2y,
		rp1.x,
		rp1.y,
		// Edge 4: right[i] → left[i] (straight cross-edge, closing)
		rp1.x,
		rp1.y,
		lp1.x,
		lp1.y,
		lp1.x,
		lp1.y,
	]
}

/**
 * Generate curved quad rawPath segments from left/right offset arrays. Each quad has Catmull-Rom bezier left/right edges and straight cross-edges. Cap polygons are appended as straight-line segments (unchanged).
 * @returns {Array} rawPath segments array
 */
export function curvedQuadsFromSides(left, right, raw, strokeCap) {
	const segments = []
	const len = Math.min(left.length, right.length)
	for (let i = 0; i < len - 1; i++) {
		segments.push(curvedSegment(left, right, i))
	}
	const endCap = buildEndCapPolygon(raw, strokeCap)
	if (endCap) segments.push(straightSegment(endCap))
	const startCap = buildStartCapPolygon(raw, strokeCap)
	if (startCap) segments.push(straightSegment(startCap))
	return segments
}

// ─── Single closed outline path ─────────────────────────────────────────────

/**
 * Build a semicircular bezier arc from p0 to p1 (diametrically opposite on a circle centered at cx,cy). Returns 2 cubic bezier arcs [c1, c2] each as [x0,y0,cp1x,cp1y,cp2x,cp2y,x1,y1]. Uses kappa constant for exact 90° circular arc approximation.
 */
function semicircleArc(x0, y0, x1, y1, cx, cy) {
	const K = 0.5522847498
	const dx = x1 - x0,
		dy = y1 - y0
	const nx = -dy,
		ny = dx
	const len = Math.hypot(nx, ny) || 1
	const r = Math.hypot(x0 - cx, y0 - cy)
	const mx = cx + (nx / len) * r
	const my = cy + (ny / len) * r
	const t0x = y0 - cy,
		t0y = -(x0 - cx)
	const tmx = my - cy,
		tmy = -(mx - cx)
	const t1x = y1 - cy,
		t1y = -(x1 - cx)
	const c1 = [x0, y0, x0 + t0x * K, y0 + t0y * K, mx - tmx * K, my - tmy * K, mx, my]
	const c2 = [mx, my, mx + tmx * K, my + tmy * K, x1 - t1x * K, y1 - t1y * K, x1, y1]
	return [c1, c2]
}

/**
 * Append polygon points as degenerate (straight-line) cubics to a flat rawPath seg array.
 */
function appendCapPoints(seg, pts) {
	for (let i = 0; i < pts.length; i++) {
		const prev = i === 0 ? { x: seg[seg.length - 2], y: seg[seg.length - 1] } : pts[i - 1]
		seg.push(prev.x, prev.y, pts[i].x, pts[i].y, pts[i].x, pts[i].y)
	}
}

/**
 * Append bezier arc cubics (from semicircleArc) to a flat rawPath seg array. Skips the start point of each arc (already the current endpoint).
 */
function appendArcCubics(seg, arcs) {
	for (const arc of arcs) {
		seg.push(arc[2], arc[3], arc[4], arc[5], arc[6], arc[7])
	}
}

/**
 * Append Catmull-Rom → cubic bezier chain for a sequence of points to a flat rawPath array.
 */
function appendCatmullRomChain(seg, points) {
	const n = points.length
	for (let i = 0; i < n - 1; i++) {
		const p0 = points[Math.max(0, i - 1)]
		const p1 = points[i]
		const p2 = points[i + 1]
		const p3 = points[Math.min(n - 1, i + 2)]
		seg.push(
			p1.x + (p2.x - p0.x) / 6,
			p1.y + (p2.y - p0.y) / 6,
			p2.x - (p3.x - p1.x) / 6,
			p2.y - (p3.y - p1.y) / 6,
			p2.x,
			p2.y
		)
	}
}

/**
 * Build a single closed outline path from left/right offset arrays. Path: left forward → end cap → right backward → start cap → close. Returns a one-element rawPath segments array.
 */
export function singleOutlineFromSides(left, right, raw, strokeCap) {
	const seg = [left[0].x, left[0].y]

	// Left edge forward
	appendCatmullRomChain(seg, left)

	// End cap
	const endCap = buildEndCapPolygon(raw, strokeCap)
	if (endCap) {
		appendCapPoints(seg, endCap)
	} else {
		const lLast = left[left.length - 1]
		const rLast = right[right.length - 1]
		seg.push(lLast.x, lLast.y, rLast.x, rLast.y, rLast.x, rLast.y)
	}

	// Right edge backward
	const rightReversed = []
	for (let i = right.length - 1; i >= 0; i--) rightReversed.push(right[i])
	appendCatmullRomChain(seg, rightReversed)

	// Start cap
	const startCap = buildStartCapPolygon(raw, strokeCap)
	if (startCap) {
		appendCapPoints(seg, startCap)
	} else {
		// Butt cap: straight line from right[0] to left[0]
		const r0 = right[0]
		const l0 = left[0]
		seg.push(r0.x, r0.y, l0.x, l0.y, l0.x, l0.y)
	}

	return [seg]
}

// ─── Outline construction helpers ──────────────────────────────────────────

/**
 * Build outline points from raw [x,y,halfWidth,...] triples.
 * @param {number[]} raw - flat [x,y,hw, x,y,hw, ...] triples
 * @param {string} strokeCap - 'round' or 'butt'
 * @returns {Array<{x:number,y:number}>} closed outline points
 */

/**
 * Remove self-intersection loops from an offset polyline (in place). Scans for pairs of non-adjacent segments that intersect, then splices out the looping points between them, inserting the intersection point.
 */
function removeLoops(pts) {
	// Only remove short loops (offset artifacts at sharp bends). Large spans are legitimate centerline self-crossings (figure-8 shapes).
	const maxSpan = Math.max(4, Math.floor(pts.length * 0.25))
	for (let i = 0; i < pts.length - 2; i++) {
		const a1 = pts[i],
			a2 = pts[i + 1]
		// Check segments starting at least 2 ahead (non-adjacent)
		for (let j = i + 2; j < pts.length - 1; j++) {
			if (j - i > maxSpan) break
			const b1 = pts[j],
				b2 = pts[j + 1]
			const ix = segIntersect(a1.x, a1.y, a2.x, a2.y, b1.x, b1.y, b2.x, b2.y)
			if (ix) {
				// Remove points from i+1 to j, replace with intersection point
				pts.splice(i + 1, j - i, { x: ix.x, y: ix.y })
				// Restart scan from this point since array changed
				i--
				break
			}
		}
	}
}

/** Test if segments (ax1,ay1)-(ax2,ay2) and (bx1,by1)-(bx2,by2) intersect.
 *  Returns {x,y} of intersection or null. */
function segIntersect(ax1, ay1, ax2, ay2, bx1, by1, bx2, by2) {
	const dx1 = ax2 - ax1,
		dy1 = ay2 - ay1
	const dx2 = bx2 - bx1,
		dy2 = by2 - by1
	const denom = dx1 * dy2 - dy1 * dx2
	if (Math.abs(denom) < 1e-10) return null
	const t = ((bx1 - ax1) * dy2 - (by1 - ay1) * dx2) / denom
	const u = ((bx1 - ax1) * dy1 - (by1 - ay1) * dx1) / denom
	if (t > 0 && t < 1 && u > 0 && u < 1) {
		return { x: ax1 + t * dx1, y: ay1 + t * dy1 }
	}
	return null
}

/**
 * Compute left/right offset polylines from [x,y,hw,...] triples. Returns { left, right, raw } — the two sides before they're merged.
 */
export function buildOutlineSides(raw) {
	const n = raw.length / 3
	if (n < 2) return null

	const left = []
	const right = []

	let prevNx = 0,
		prevNy = 0
	for (let i = 0; i < n; i++) {
		const x = raw[i * 3]
		const y = raw[i * 3 + 1]
		const hw = raw[i * 3 + 2]

		let tx, ty
		if (i === 0) {
			tx = raw[3] - x
			ty = raw[4] - y
		} else if (i === n - 1) {
			tx = x - raw[(i - 1) * 3]
			ty = y - raw[(i - 1) * 3 + 1]
		} else {
			tx = raw[(i + 1) * 3] - raw[(i - 1) * 3]
			ty = raw[(i + 1) * 3 + 1] - raw[(i - 1) * 3 + 1]
		}
		const len = Math.hypot(tx, ty) || 1
		let nx = -ty / len
		let ny = tx / len

		if (i > 0 && nx * prevNx + ny * prevNy < 0) {
			nx = -nx
			ny = -ny
		}
		prevNx = nx
		prevNy = ny

		left.push({ x: x + nx * hw, y: y + ny * hw })
		right.push({ x: x - nx * hw, y: y - ny * hw })
	}

	return { left, right, raw }
}

/**
 * Build outline left/right sides directly from a bezier centerline segment. Computes normals analytically from bezierDeriv — no dense resampling needed.
 * @param {number[]} segment - rawPath segment [a0x, a0y, cp1x, cp1y, cp2x, cp2y, a1x, a1y, ...]
 * @param {number} baseHalfWidth - maximum half-width at center
 * @param {number} [minHalfWidth=0] - minimum half-width at edges
 * @param {number} [interval=6] - approximate spacing between samples (px)
 * @returns {{ left: Array, right: Array, raw: number[] } | null}
 */
function _buildOutlineSidesFromBezier(segment, baseHalfWidth, minHalfWidth = 0, _interval = 6) {
	if (segment.length < 8) return null

	// Step 1: estimate total arc length
	let totalLen = 0
	const cubicLens = []
	for (let i = 2; i < segment.length; i += 6) {
		const x0 = i === 2 ? segment[0] : segment[i - 2]
		const y0 = i === 2 ? segment[1] : segment[i - 1]
		const cp1x = segment[i],
			cp1y = segment[i + 1]
		const cp2x = segment[i + 2],
			cp2y = segment[i + 3]
		const x1 = segment[i + 4],
			y1 = segment[i + 5]
		const chord = Math.hypot(x1 - x0, y1 - y0)
		const ctrl =
			Math.hypot(cp1x - x0, cp1y - y0) + Math.hypot(cp2x - cp1x, cp2y - cp1y) + Math.hypot(x1 - cp2x, y1 - cp2y)
		const len = (chord + ctrl) / 2
		cubicLens.push(len)
		totalLen += len
	}

	if (totalLen < 1e-6) return null

	// Short-stroke clamp (same as assignPositionWidths)
	const effectiveHW = totalLen < baseHalfWidth * 4 ? totalLen / 4 : baseHalfWidth

	const left = []
	const right = []
	const triples = [] // flat [x, y, hw, ...] for cap construction
	let prevNx = 0,
		prevNy = 0

	const FLAT_TOL = 1.5 // px — flatness threshold for subdivision
	const WIDTH_TOL = 1.5 // px — max half-width change before subdividing
	const MAX_DEPTH = 8

	// Emit a sample at a point on the centerline
	function emitPoint(curve, t, arcLen) {
		const [ax, ay, c1x, c1y, c2x, c2y, bx, by] = curve
		const [px, py] = bezierEval(ax, ay, c1x, c1y, c2x, c2y, bx, by, t)
		const [dx, dy] = bezierDeriv(ax, ay, c1x, c1y, c2x, c2y, bx, by, t)
		const dLen = Math.hypot(dx, dy) || 1
		let nx = -dy / dLen
		let ny = dx / dLen

		if (left.length > 0 && nx * prevNx + ny * prevNy < 0) {
			nx = -nx
			ny = -ny
		}
		prevNx = nx
		prevNy = ny

		const arcRatio = arcLen / totalLen
		const hw = minHalfWidth + (effectiveHW - minHalfWidth) * Math.sin(Math.PI * arcRatio)

		left.push({ x: px + nx * hw, y: py + ny * hw })
		right.push({ x: px - nx * hw, y: py - ny * hw })
		triples.push(px, py, hw)
	}

	// Adaptively subdivide a cubic and emit samples at leaf endpoints
	function subdivide(curve, arcStart, arcLen, depth) {
		const [ax, ay, c1x, c1y, c2x, c2y, bx, by] = curve
		const flat = cubicFlatness(ax, ay, c1x, c1y, c2x, c2y, bx, by)

		// Check width change across this piece
		const r0 = arcStart / totalLen
		const r1 = (arcStart + arcLen) / totalLen
		const hw0 = minHalfWidth + (effectiveHW - minHalfWidth) * Math.sin(Math.PI * r0)
		const hw1 = minHalfWidth + (effectiveHW - minHalfWidth) * Math.sin(Math.PI * r1)
		const widthChange = Math.abs(hw1 - hw0)

		if (depth < MAX_DEPTH && (flat > FLAT_TOL || widthChange > WIDTH_TOL)) {
			const { left: lh, right: rh } = splitBezierAt(curve, 0.5)
			const halfLen = arcLen / 2
			subdivide(lh, arcStart, halfLen, depth + 1)
			subdivide(rh, arcStart + halfLen, halfLen, depth + 1)
		} else {
			// Emit endpoint of this piece
			emitPoint(curve, 1, arcStart + arcLen)
		}
	}

	// Emit first point, then adaptively sample each centerline cubic
	let cumLen = 0
	let cubicIdx = 0
	for (let i = 2; i < segment.length; i += 6, cubicIdx++) {
		const x0 = i === 2 ? segment[0] : segment[i - 2]
		const y0 = i === 2 ? segment[1] : segment[i - 1]
		const curve = [
			x0,
			y0,
			segment[i],
			segment[i + 1],
			segment[i + 2],
			segment[i + 3],
			segment[i + 4],
			segment[i + 5],
		]

		if (cubicIdx === 0) {
			emitPoint(curve, 0, 0) // first point of entire path
		}

		subdivide(curve, cumLen, cubicLens[cubicIdx], 0)
		cumLen += cubicLens[cubicIdx]
	}

	if (left.length < 2) return null

	return { left, right, raw: triples }
}

/**
 * Build complete outline segments from a bezier centerline. Combines analytical normal computation with single closed bezier outline.
 * @param {number[]} segment - rawPath segment from schneiderFit
 * @param {number} baseHalfWidth - maximum half-width
 * @param {number} [minHalfWidth=0] - minimum half-width
 * @param {string} [strokeCap='round'] - 'round' or 'butt'
 * @param {number} [interval=6] - sample spacing
 * @returns {Array|null} rawPath segments for outline
 */
export function buildOutlineFromBezier(
	segment,
	baseHalfWidth,
	minHalfWidth = 0,
	strokeCap = 'round',
	_interval = 6,
	widthProfile = 'diamond',
	capStart,
	capEnd,
	hwAtFn
) {
	if (segment.length < 8) return null

	// Step 1: estimate total arc length for width tapering
	let totalLen = 0
	const cubicLens = []
	const cubics = []
	for (let i = 2; i < segment.length; i += 6) {
		const x0 = i === 2 ? segment[0] : segment[i - 2]
		const y0 = i === 2 ? segment[1] : segment[i - 1]
		const c = [x0, y0, segment[i], segment[i + 1], segment[i + 2], segment[i + 3], segment[i + 4], segment[i + 5]]
		cubics.push(c)
		const chord = Math.hypot(c[6] - c[0], c[7] - c[1])
		const ctrl =
			Math.hypot(c[2] - c[0], c[3] - c[1]) +
			Math.hypot(c[4] - c[2], c[5] - c[3]) +
			Math.hypot(c[6] - c[4], c[7] - c[5])
		const len = (chord + ctrl) / 2
		cubicLens.push(len)
		totalLen += len
	}
	if (totalLen < 1e-6) return null

	const effectiveHW = totalLen < baseHalfWidth * 4 ? totalLen / 4 : baseHalfWidth

	// Step 2: offset each centerline cubic
	const allLeft = [] // array of cubic [8] arrays
	const allRight = []
	const triples = [] // [x, y, hw, ...] for caps
	let cumLen = 0

	function hwAt(arcLen) {
		if (hwAtFn) return hwAtFn(arcLen, totalLen)
		const r = arcLen / totalLen
		let factor
		if (widthProfile === 'grow') {
			factor = r
		} else if (widthProfile === 'shrink') {
			factor = 1 - r
		} else {
			factor = Math.sin(Math.PI * r)
		}
		return minHalfWidth + (effectiveHW - minHalfWidth) * factor
	}

	for (let ci = 0; ci < cubics.length; ci++) {
		const c = cubics[ci]
		const arcStart = cumLen
		const arcLen = cubicLens[ci]

		// Subdivide this cubic into the same pieces offsetCubicAdaptive would, but compute per-piece hw from the arc position
		function subdividePieces(curve, aStart, aLen, depth) {
			const [cx0, cy0, ccp1x, ccp1y, ccp2x, ccp2y, cx1, cy1] = curve
			let d0x = ccp1x - cx0,
				d0y = ccp1y - cy0
			if (Math.hypot(d0x, d0y) < 1e-10) {
				d0x = ccp2x - cx0
				d0y = ccp2y - cy0
			}
			if (Math.hypot(d0x, d0y) < 1e-10) {
				d0x = cx1 - cx0
				d0y = cy1 - cy0
			}
			let d1x = cx1 - ccp2x,
				d1y = cy1 - ccp2y
			if (Math.hypot(d1x, d1y) < 1e-10) {
				d1x = cx1 - ccp1x
				d1y = cy1 - ccp1y
			}
			if (Math.hypot(d1x, d1y) < 1e-10) {
				d1x = cx1 - cx0
				d1y = cy1 - cy0
			}
			const angle = angleBetween(d0x, d0y, d1x, d1y)

			if (angle > OFFSET_MAX_ANGLE && depth < OFFSET_MAX_DEPTH) {
				const { left: lh, right: rh } = splitBezierAt(curve, 0.5)
				const halfLen = aLen / 2
				subdividePieces(lh, aStart, halfLen, depth + 1)
				subdividePieces(rh, aStart + halfLen, halfLen, depth + 1)
			} else {
				const hw0 = hwAt(aStart)
				const hw1 = hwAt(aStart + aLen * (1 / 3))
				const hw2 = hwAt(aStart + aLen * (2 / 3))
				const hw3 = hwAt(aStart + aLen)
				const result = offsetCubic(curve, hw0, hw1, hw2, hw3)
				allLeft.push(result.left[0])
				allRight.push(result.right[0])
			}
		}
		subdividePieces(c, arcStart, arcLen, 0)

		cumLen += arcLen
	}

	if (!allLeft.length) return null

	// Build triples for cap construction Caps need tangent direction — use points near (not at) the tips where hw > 0
	{
		const first = cubics[0]
		const last = cubics[cubics.length - 1]
		// Start cap: use hw at a small arc offset so it's nonzero
		const startHW = hwAt(totalLen * 0.02)
		const [sx, sy] = bezierEval(first[0], first[1], first[2], first[3], first[4], first[5], first[6], first[7], 0.1)
		triples.push(first[0], first[1], startHW)
		triples.push(sx, sy, startHW)
		// End cap: use hw at a small arc offset from the end
		const endHW = hwAt(totalLen * 0.98)
		const [ex, ey] = bezierEval(last[0], last[1], last[2], last[3], last[4], last[5], last[6], last[7], 0.9)
		triples.push(ex, ey, endHW)
		triples.push(last[6], last[7], endHW)
	}

	// Step 3: assemble closed outline rawPath left forward → end cap → right backward → start cap
	const firstLeft = allLeft[0]
	const seg = [firstLeft[0], firstLeft[1]]

	// Append left cubics forward
	for (const lc of allLeft) {
		seg.push(lc[2], lc[3], lc[4], lc[5], lc[6], lc[7])
	}

	// End cap
	const lastLeft = allLeft[allLeft.length - 1]
	const lastRight = allRight[allRight.length - 1]
	const endHw = triples[(triples.length / 3 - 1) * 3 + 2]
	if ((capEnd ?? strokeCap) === 'round' && endHw > 0.5) {
		const endCapCx = (lastLeft[6] + lastRight[6]) / 2
		const endCapCy = (lastLeft[7] + lastRight[7]) / 2
		appendArcCubics(seg, semicircleArc(lastLeft[6], lastLeft[7], lastRight[6], lastRight[7], endCapCx, endCapCy))
	} else {
		seg.push(lastLeft[6], lastLeft[7], lastRight[6], lastRight[7], lastRight[6], lastRight[7])
	}

	// Append right cubics backward
	for (let i = allRight.length - 1; i >= 0; i--) {
		const rc = allRight[i]
		// Backward: from endpoint to start, so reverse the cubic
		seg.push(rc[4], rc[5], rc[2], rc[3], rc[0], rc[1])
	}

	// Start cap
	const firstRight = allRight[0]
	const startHw = triples[2]
	if ((capStart ?? strokeCap) === 'round' && startHw > 0.5) {
		const startCapCx = (firstLeft[0] + firstRight[0]) / 2
		const startCapCy = (firstLeft[1] + firstRight[1]) / 2
		appendArcCubics(
			seg,
			semicircleArc(firstRight[0], firstRight[1], firstLeft[0], firstLeft[1], startCapCx, startCapCy)
		)
	} else {
		seg.push(firstRight[0], firstRight[1], firstLeft[0], firstLeft[1], firstLeft[0], firstLeft[1])
	}

	seg.closed = true
	return [seg]
}

/**
 * Build a round end-cap fan polygon. Sweeps a semicircle from the left side to the right side at the last point.
 */
function buildEndCapPolygon(raw, strokeCap) {
	const n = raw.length / 3
	const lastIdx = n - 1
	const hw = raw[lastIdx * 3 + 2]
	if (strokeCap !== 'round' || hw <= 0.5) return null
	const cx = raw[lastIdx * 3]
	const cy = raw[lastIdx * 3 + 1]
	const tx = cx - raw[(lastIdx - 1) * 3]
	const ty = cy - raw[(lastIdx - 1) * 3 + 1]
	const tLen = Math.hypot(tx, ty) || 1
	const nx = -ty / tLen
	const ny = tx / tLen
	const pts = [{ x: cx + nx * hw, y: cy + ny * hw }]
	for (let i = 1; i < 8; i++) {
		const a = (Math.PI * i) / 8
		pts.push({
			x: cx + (nx * Math.cos(a) + (tx / tLen) * Math.sin(a)) * hw,
			y: cy + (ny * Math.cos(a) + (ty / tLen) * Math.sin(a)) * hw,
		})
	}
	pts.push({ x: cx - nx * hw, y: cy - ny * hw })
	return pts
}

/**
 * Build a round start-cap fan polygon. Sweeps a semicircle from the right side to the left side at the first point.
 */
function buildStartCapPolygon(raw, strokeCap) {
	const hw = raw[2]
	if (strokeCap !== 'round' || hw <= 0.5) return null
	const cx = raw[0]
	const cy = raw[1]
	const stx = raw[3] - cx
	const sty = raw[4] - cy
	const sLen = Math.hypot(stx, sty) || 1
	const snx = -sty / sLen
	const sny = stx / sLen
	const pts = [{ x: cx - snx * hw, y: cy - sny * hw }]
	for (let i = 1; i < 8; i++) {
		const a = (Math.PI * i) / 8
		pts.push({
			x: cx + (-snx * Math.cos(a) + (-stx / sLen) * Math.sin(a)) * hw,
			y: cy + (-sny * Math.cos(a) + (-sty / sLen) * Math.sin(a)) * hw,
		})
	}
	pts.push({ x: cx + snx * hw, y: cy + sny * hw })
	return pts
}

/**
 * Generate quad-strip polygons from left/right offset arrays. Each quad connects adjacent points: [left[i], left[i+1], right[i+1], right[i]]. Also appends round cap polygons at endpoints if applicable.
 * @returns {Array<Array<{x:number,y:number}>>} array of simple polygons
 */
/**
 * Build a rawPath segment from polygon points using straight lines. Each edge becomes a degenerate cubic where control points equal endpoints.
 */
export function straightSegment(pts) {
	const seg = [pts[0].x, pts[0].y]
	const closed = [...pts, pts[0]]
	for (let i = 1; i < closed.length; i++) {
		seg.push(closed[i - 1].x, closed[i - 1].y, closed[i].x, closed[i].y, closed[i].x, closed[i].y)
	}
	return seg
}

export function quadsFromSides(left, right, raw, strokeCap) {
	const quads = []
	const len = Math.min(left.length, right.length)
	for (let i = 0; i < len - 1; i++) {
		quads.push([left[i], left[i + 1], right[i + 1], right[i]])
	}
	const endCap = buildEndCapPolygon(raw, strokeCap)
	if (endCap) quads.push(endCap)
	const startCap = buildStartCapPolygon(raw, strokeCap)
	if (startCap) quads.push(startCap)
	return quads
}

export function buildOutlinePoints(raw, strokeCap) {
	const sides = buildOutlineSides(raw)
	if (!sides) return null
	const { left, right } = sides
	const n = raw.length / 3

	// Remove offset loops for single-polygon assembly
	removeLoops(left)
	removeLoops(right)

	// Assemble: left forward + end cap + right reversed + start cap
	const outline = [...left]

	// End cap (hw is ~0 at endpoints with position-based width, but keep for strokeCap=round)
	const lastIdx = n - 1
	const lastHw = raw[lastIdx * 3 + 2]
	const lastX = raw[lastIdx * 3]
	const lastY = raw[lastIdx * 3 + 1]
	if (strokeCap === 'round' && lastHw > 0.5) {
		const etx = lastX - raw[(lastIdx - 1) * 3]
		const ety = lastY - raw[(lastIdx - 1) * 3 + 1]
		const eLen = Math.hypot(etx, ety) || 1
		const enx = -ety / eLen
		const eny = etx / eLen
		for (let i = 1; i < 8; i++) {
			const a = (Math.PI * i) / 8
			outline.push({
				x: lastX + (enx * Math.cos(a) + (etx / eLen) * Math.sin(a)) * lastHw,
				y: lastY + (eny * Math.cos(a) + (ety / eLen) * Math.sin(a)) * lastHw,
			})
		}
	}

	for (let i = right.length - 1; i >= 0; i--) outline.push(right[i])

	// Start cap
	const firstHw = raw[2]
	const firstX = raw[0]
	const firstY = raw[1]
	if (strokeCap === 'round' && firstHw > 0.5) {
		const stx = raw[3] - firstX
		const sty = raw[4] - firstY
		const sLen = Math.hypot(stx, sty) || 1
		const snx = -sty / sLen
		const sny = stx / sLen
		for (let i = 1; i < 8; i++) {
			const a = (Math.PI * i) / 8
			outline.push({
				x: firstX + (-snx * Math.cos(a) + (-stx / sLen) * Math.sin(a)) * firstHw,
				y: firstY + (-sny * Math.cos(a) + (-sty / sLen) * Math.sin(a)) * firstHw,
			})
		}
	}

	return outline
}

/**
 * Build quad-strip rawPath segments for real-time preview. Uses EMA-smoothed centerline → assign widths → quad strip → straightSegment. Fast (O(n), no bezier fitting) and matches the final shape exactly.
 * @param {number[]} raw - flat [x,y,...] centerline pairs
 * @param {number} smoothness - 0..100
 * @param {string} strokeCap - 'round' or 'butt'
 * @param {number} strokeWidth - base brush width
 * @returns {Array|null} rawPath segments array
 */
export function buildOutlineSegments(raw, smoothness, strokeCap, strokeWidth, minWidth = 0, widthProfile) {
	if (raw.length < 4) return null
	const segment = schneiderFit(raw, smoothness)
	return buildOutlineFromBezier(segment, strokeWidth / 2, minWidth / 2, strokeCap, 6, widthProfile)
}

/**
 * Fit a variable-width brush from centerline [x,y,...] pairs into a closed bezier outline. Width is position-based: 0 at edges, max at center (sin curve). Also produces a fitted centerline path for editing.
 * @param {number[]} raw - flat [x,y, ...] centerline pairs
 * @param {number} smoothness - 0..100
 * @param {string} strokeCap - 'round' or 'butt'
 * @param {number} strokeWidth - base brush width
 * @returns {{ data: string, centerline: string, x: number, y: number } | null}
 */

/**
 * Normalize a brush update so outline data has no negative coordinates. Shifts outline + centerline by the outline's min coords, adjusts x/y. Call on commit (not during drag) to ensure getObjectBounds returns x=obj.x.
 * @param {{ data: string, centerline?: string, x: number, y: number }} update
 * @returns {{ data: string, centerline?: string, x: number, y: number }} — same object, mutated
 */
export function normalizeBrushOutline(update) {
	const rawPath = stringToRawPath(update.data)
	// Find min coords of outline
	let minX = Infinity,
		minY = Infinity
	for (const seg of rawPath) {
		for (let i = 0; i < seg.length; i += 2) {
			if (seg[i] < minX) minX = seg[i]
			if (seg[i + 1] < minY) minY = seg[i + 1]
		}
	}
	const shiftX = Math.min(0, minX)
	const shiftY = Math.min(0, minY)
	if (shiftX >= -0.01 && shiftY >= -0.01) return update
	// Shift outline
	for (const seg of rawPath) {
		for (let i = 0; i < seg.length; i++) {
			seg[i] -= i % 2 ? shiftY : shiftX
		}
	}
	update.data = rawPathToString(rawPath)
	update.x += shiftX
	update.y += shiftY
	// Shift centerline by same amount
	if (update.centerline) {
		const clPath = stringToRawPath(update.centerline)
		for (const seg of clPath) {
			for (let i = 0; i < seg.length; i++) {
				seg[i] -= i % 2 ? shiftY : shiftX
			}
		}
		update.centerline = rawPathToString(clPath)
	}
	return update
}

export function fitVariableWidthBrush(raw, smoothness, strokeCap, strokeWidth, minWidth = 0, widthProfile) {
	if (raw.length < 4) return null

	// Fit centerline bezier via Schneider curve fitting
	const clSegment = schneiderFit(raw, smoothness)

	// Build outline directly from bezier centerline (analytical normals)
	const segments = buildOutlineFromBezier(clSegment, strokeWidth / 2, minWidth / 2, strokeCap, 6, widthProfile)
	if (!segments) return null

	// Use first raw point as stable origin (preserved exactly by schneiderFit)
	const ox = Math.round(raw[0] * 10) / 10
	const oy = Math.round(raw[1] * 10) / 10

	for (const seg of segments) {
		for (let i = 0; i < seg.length; i++) {
			seg[i] -= i % 2 ? oy : ox
		}
	}
	const data = rawPathToString(segments)

	// Shift centerline by the same origin offset so both are in local space
	const clShifted = clSegment.slice()
	for (let i = 0; i < clShifted.length; i++) {
		clShifted[i] -= i % 2 ? oy : ox
	}
	const centerlineLocal = rawPathToString([clShifted])

	return { data, x: ox, y: oy, centerline: centerlineLocal }
}

// ─── Sample a centerline path back to flat pairs ──────────────────────────

/**
 * Sample a cubic bezier curve at regular intervals.
 */
function sampleCubicBezier(x0, y0, cp1x, cp1y, cp2x, cp2y, x1, y1, steps) {
	const points = []
	for (let i = 0; i <= steps; i++) {
		const t = i / steps
		const t2 = t * t
		const t3 = t2 * t
		const mt = 1 - t
		const mt2 = mt * mt
		const mt3 = mt2 * mt
		points.push({
			x: mt3 * x0 + 3 * mt2 * t * cp1x + 3 * mt * t2 * cp2x + t3 * x1,
			y: mt3 * y0 + 3 * mt2 * t * cp1y + 3 * mt * t2 * cp2y + t3 * y1,
		})
	}
	return points
}

/**
 * Sample a bezier path string to flat [x,y,...] pairs at regular intervals.
 * @param {string} pathData - SVG path string (centerline)
 * @returns {number[]} flat [x,y,x,y,...] pairs
 */
export function sampleCenterline(pathData) {
	const rawPath = stringToRawPath(pathData)
	const interval = 4
	const samples = []

	for (let s = 0; s < rawPath.length; s++) {
		const seg = rawPath[s]
		if (s === 0) samples.push(seg[0], seg[1])
		for (let i = 2; i < seg.length; i += 6) {
			const x0 = i === 2 ? seg[0] : seg[i - 2]
			const y0 = i === 2 ? seg[1] : seg[i - 1]
			const cp1x = seg[i],
				cp1y = seg[i + 1]
			const cp2x = seg[i + 2],
				cp2y = seg[i + 3]
			const x1 = seg[i + 4],
				y1 = seg[i + 5]
			const chordLen = Math.hypot(x1 - x0, y1 - y0)
			const ctrlLen =
				Math.hypot(cp1x - x0, cp1y - y0) +
				Math.hypot(cp2x - cp1x, cp2y - cp1y) +
				Math.hypot(x1 - cp2x, y1 - cp2y)
			const approxLen = (chordLen + ctrlLen) / 2
			const steps = Math.max(2, Math.ceil(approxLen / interval))
			const pts = sampleCubicBezier(x0, y0, cp1x, cp1y, cp2x, cp2y, x1, y1, steps)
			for (let j = 1; j < pts.length; j++)
				samples.push(Math.round(pts[j].x * 10) / 10, Math.round(pts[j].y * 10) / 10)
		}
	}

	return samples
}

/**
 * Build outline from an existing centerline path without re-fitting the centerline. Used by the path editor to regenerate the outline after anchor edits. The centerline has already been edited by hand, so we don't simplify it — just sample it, assign widths, and build the outline.
 * @param {string} centerlineData - SVG path string (centerline, in absolute coords)
 * @param {number} strokeWidth - base brush width
 * @param {string} strokeCap - 'round' or 'butt'
 * @param {number} smoothness - 0..100
 * @returns {{ data: string, x: number, y: number } | null}
 */
export function buildOutlineFromCenterline(
	centerlineData,
	strokeWidth,
	strokeCap,
	smoothness,
	minWidth = 0,
	widthProfile
) {
	const rawPath = stringToRawPath(centerlineData)
	if (!rawPath.length || rawPath[0].length < 8) return null

	// Walk each bezier segment directly — no resampling or refitting For multi-segment paths, concatenate into one segment for outline continuity
	const combined = rawPath[0].slice()
	for (let s = 1; s < rawPath.length; s++) {
		combined.push(...rawPath[s].slice(2)) // skip moveTo, shared anchor
	}

	const segments = buildOutlineFromBezier(combined, strokeWidth / 2, minWidth / 2, strokeCap, 6, widthProfile)
	if (!segments) return null

	const ox = Math.round(combined[0] * 10) / 10
	const oy = Math.round(combined[1] * 10) / 10

	for (const seg of segments) {
		for (let i = 0; i < seg.length; i++) {
			seg[i] -= i % 2 ? oy : ox
		}
	}
	return { data: rawPathToString(segments), x: ox, y: oy }
}

/**
 * Sample a rawPath array (not string) to flat [x,y,...] pairs. Same as sampleCenterline but accepts parsed rawPath directly.
 */
export function sampleCenterlineRaw(rawPath) {
	const interval = 4
	const samples = []
	for (let s = 0; s < rawPath.length; s++) {
		const seg = rawPath[s]
		if (s === 0) samples.push(seg[0], seg[1])
		for (let i = 2; i < seg.length; i += 6) {
			const x0 = i === 2 ? seg[0] : seg[i - 2]
			const y0 = i === 2 ? seg[1] : seg[i - 1]
			const cp1x = seg[i],
				cp1y = seg[i + 1]
			const cp2x = seg[i + 2],
				cp2y = seg[i + 3]
			const x1 = seg[i + 4],
				y1 = seg[i + 5]
			const chordLen = Math.hypot(x1 - x0, y1 - y0)
			const ctrlLen =
				Math.hypot(cp1x - x0, cp1y - y0) +
				Math.hypot(cp2x - cp1x, cp2y - cp1y) +
				Math.hypot(x1 - cp2x, y1 - cp2y)
			const approxLen = (chordLen + ctrlLen) / 2
			const steps = Math.max(2, Math.ceil(approxLen / interval))
			const pts = sampleCubicBezier(x0, y0, cp1x, cp1y, cp2x, cp2y, x1, y1, steps)
			for (let j = 1; j < pts.length; j++)
				samples.push(Math.round(pts[j].x * 10) / 10, Math.round(pts[j].y * 10) / 10)
		}
	}
	return samples
}

/**
 * Build outline rawPath segments from a centerline rawPath array (not string). Used during transitions to rebuild the brush outline each frame without string round-trips.
 * @param {Array} clRawPath - parsed centerline rawPath
 * @param {number} strokeWidth - base brush width
 * @param {string} strokeCap - 'round' or 'butt'
 * @param {number} [minWidth=0] - minimum width at edges
 * @returns {Array|null} rawPath segments array for the outline
 */
export function buildOutlineSegmentsFromCenterlineRaw(clRawPath, strokeWidth, strokeCap, minWidth = 0, widthProfile) {
	if (!clRawPath.length || clRawPath[0].length < 8) return null
	// Concatenate multi-segment rawPath into one for outline continuity
	const combined = clRawPath[0].slice()
	for (let s = 1; s < clRawPath.length; s++) {
		combined.push(...clRawPath[s].slice(2))
	}
	return buildOutlineFromBezier(combined, strokeWidth / 2, minWidth / 2, strokeCap, 6, widthProfile)
}

// ─── Brush edit anchors (simplified editing with reduced control points) ───

/**
 * Sample N evenly arc-length-spaced points along a centerline bezier path. Returns flat [x,y,x,y,...] pairs. Always includes first and last points.
 * @param {string} pathData - SVG path string (centerline)
 * @param {number} [count] - number of edit anchors (auto-computed from arc length if omitted)
 * @returns {number[]} flat [x,y,x,y,...] pairs
 */
export function sampleEditAnchors(pathData, count) {
	// First densely sample the centerline
	const dense = sampleCenterline(pathData)
	const n = dense.length / 2
	if (n < 3) return dense.slice()

	// Compute cumulative arc lengths
	const arcLengths = [0]
	for (let i = 1; i < n; i++) {
		const dx = dense[i * 2] - dense[(i - 1) * 2]
		const dy = dense[i * 2 + 1] - dense[(i - 1) * 2 + 1]
		arcLengths.push(arcLengths[i - 1] + Math.hypot(dx, dy))
	}
	const totalLength = arcLengths[n - 1]
	if (totalLength < 1) return [dense[0], dense[1], dense[(n - 1) * 2], dense[(n - 1) * 2 + 1]]

	// Auto-compute anchor count if not specified
	if (!count) count = Math.max(3, Math.min(15, Math.round(totalLength / 40)))

	// Helper: interpolate position at a given arc length along the dense polyline
	const posAtArcLen = target => {
		let lo = 0,
			hi = n - 1
		while (lo < hi - 1) {
			const mid = (lo + hi) >> 1
			if (arcLengths[mid] < target) lo = mid
			else hi = mid
		}
		const segLen = arcLengths[hi] - arcLengths[lo]
		const t = segLen > 0 ? (target - arcLengths[lo]) / segLen : 0
		return [
			dense[lo * 2] + t * (dense[hi * 2] - dense[lo * 2]),
			dense[lo * 2 + 1] + t * (dense[hi * 2 + 1] - dense[lo * 2 + 1]),
		]
	}

	// Start with arc-length-evenly-spaced positions
	const arcParams = new Float64Array(count)
	for (let k = 0; k < count; k++) arcParams[k] = (k / (count - 1)) * totalLength

	// Relaxation: adjust interior arc-length params to equalize chord distances. On curved paths, equal arc-length != equal chord distance; this iteratively slides anchors toward the midpoint (by chord) of their neighbors.
	for (let iter = 0; iter < 8; iter++) {
		// Compute current positions
		const pos = []
		for (let k = 0; k < count; k++) {
			const p =
				k === 0
					? [dense[0], dense[1]]
					: k === count - 1
						? [dense[(n - 1) * 2], dense[(n - 1) * 2 + 1]]
						: posAtArcLen(arcParams[k])
			pos.push(p)
		}
		// Compute chord distances
		const chords = []
		for (let k = 0; k < count - 1; k++) {
			chords.push(Math.hypot(pos[k + 1][0] - pos[k][0], pos[k + 1][1] - pos[k][1]))
		}
		// Adjust each interior anchor's arc param to equalize chord distances. If dPrev > dNext, anchor is too far from prev → shift toward prev (lower arc).
		let maxShift = 0
		for (let k = 1; k < count - 1; k++) {
			const dPrev = chords[k - 1]
			const dNext = chords[k]
			const imbalance = (dPrev - dNext) / (dPrev + dNext + 0.01)
			// Shift arc param proportionally to imbalance and available range
			const range = arcParams[k + 1] - arcParams[k - 1]
			const shift = -imbalance * range * 0.25
			arcParams[k] += shift
			// Clamp to stay between neighbors
			arcParams[k] = Math.max(arcParams[k - 1] + 0.1, Math.min(arcParams[k + 1] - 0.1, arcParams[k]))
			if (Math.abs(shift) > maxShift) maxShift = Math.abs(shift)
		}
		if (maxShift < 0.1) break // converged
	}

	// Build final result from relaxed arc params
	const result = [dense[0], dense[1]]
	for (let k = 1; k < count - 1; k++) {
		const p = posAtArcLen(Math.max(0, Math.min(totalLength, arcParams[k])))
		result.push(p[0], p[1])
	}
	result.push(dense[(n - 1) * 2], dense[(n - 1) * 2 + 1])
	return result
}

/**
 * Fit a smooth curve through edit anchor positions using Catmull-Rom (via pointsToSegment). Returns a centerline path string suitable for buildOutlineFromCenterline.
 * @param {number[]} anchors - flat [x,y,x,y,...] edit anchor positions
 * @param {number} smoothness - 0..100
 * @returns {string} SVG path string
 */
export function interpolateEditAnchors(anchors, smoothness) {
	if (anchors.length < 4) return ''
	const curviness = smoothCurviness(smoothness)
	const segment = pointsToSegment(anchors, curviness)
	smoothHandles(segment)
	return rawPathToString([segment])
}
